import { useState } from "react";
import { MessageSquarePlus, X, Send, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { API } from "../lib/helpers";
import axios from "axios";

const TYPES = [
  { id: "bug", label: "Bug Report" },
  { id: "feature", label: "Feature Request" },
  { id: "general", label: "General" },
];

const FeedbackWidget = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        user_id: user?.email || "anonymous",
        user_email: user?.email || "",
        type,
        message: message.trim(),
        page: window.location.pathname,
        rating: rating || null,
      });
      toast.success("Thanks for your feedback!");
      setMessage("");
      setRating(0);
      setIsOpen(false);
    } catch {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20 flex items-center justify-center transition-all hover:scale-105"
        data-testid="feedback-trigger"
      >
        {isOpen ? <X className="w-4 h-4 sm:w-5 sm:h-5" /> : <MessageSquarePlus className="w-4 h-4 sm:w-5 sm:h-5" />}
      </button>

      {/* Feedback panel */}
      {isOpen && (
        <div className="fixed bottom-16 right-4 sm:bottom-20 sm:right-6 z-50 w-[calc(100vw-32px)] sm:w-80 bg-card border border-border rounded-xl shadow-2xl overflow-hidden animate-slide-up" data-testid="feedback-panel">
          <div className="px-4 py-3 border-b border-border bg-secondary/30">
            <h3 className="text-sm font-semibold">Send Feedback</h3>
            <p className="text-[10px] text-muted-foreground">Help us improve RealixData</p>
          </div>

          <div className="p-4 space-y-3">
            {/* Type selector */}
            <div className="flex gap-1.5">
              {TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={`flex-1 text-xs py-1.5 rounded-md border transition-colors ${
                    type === t.id
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500"
                      : "bg-secondary/30 border-border text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`feedback-type-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Message */}
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind?"
              className="bg-secondary/20 border-border text-sm min-h-[80px] resize-none"
              data-testid="feedback-message"
            />

            {/* Star rating */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground mr-1">Rating:</span>
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHoveredStar(s)}
                  onMouseLeave={() => setHoveredStar(0)}
                  data-testid={`feedback-star-${s}`}
                >
                  <Star className={`w-4 h-4 transition-colors ${
                    s <= (hoveredStar || rating)
                      ? "fill-amber-500 text-amber-500"
                      : "text-muted-foreground/30"
                  }`} />
                </button>
              ))}
            </div>

            {/* Submit */}
            <Button
              size="sm"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleSubmit}
              disabled={!message.trim() || submitting}
              data-testid="feedback-submit"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {submitting ? "Sending..." : "Send Feedback"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
